import { DeckReleases } from './DeckReleases';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n';

interface Version {
  id: number;
  label: string;
  title: string | null;
  is_current: boolean;
  item_count: number;
}

interface Item {
  id: number;
  card_number: number;
  name: string;
  category: string | null;
  metalabel: string | null;
  change_type: string | null;
  topic_active: boolean | null;
  successor_number: number | null;
  successor_name: string | null;
  successor_version: string | null;
}

export function CardVersionsPanel() {
  const { t } = useT();
  const [versions, setVersions] = useState<Version[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get<{ versions: Version[] }>('/api/admin/card-versions').then((r) => {
      setVersions(r.versions);
      // default to the current version
      const cur = r.versions.find((v) => v.is_current) ?? r.versions[r.versions.length - 1];
      if (cur) setSelected(cur.id);
    });
  }, []);

  useEffect(() => {
    if (selected == null) return;
    setLoading(true);
    api.get<{ items: Item[] }>(`/api/admin/card-versions/${selected}/inventory`)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  }, [selected]);

  const changeLabel = (c: string | null) =>
    c === 'unchanged' ? t('cardver.unchanged')
      : c === 'renumbered' ? t('cardver.renumbered')
      : c === 'dropped' ? t('cardver.dropped')
      : c ?? '';

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{t('cardver.title')}</h2>
      <p className="save-indicator" style={{ marginTop: 0 }}>{t('cardver.intro')}</p>

      <DeckReleases versions={versions} />
      <div className="tabs" style={{ marginBottom: 16 }}>
        {versions.map((v) => (
          <button
            key={v.id}
            className={`tab ${selected === v.id ? 'tab--active' : ''}`}
            onClick={() => setSelected(v.id)}
          >
            {v.title || v.label} · {v.item_count} {t('cardver.items')}
            {v.is_current && <span className="badge badge-accent" style={{ marginLeft: 6 }}>{t('cardver.current')}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="save-indicator">{t('cardver.loading')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-bordered" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 44 }}>{t('cardver.number')}</th>
                <th>{t('cardver.name')}</th>
                <th>{t('cardver.category')}</th>
                <th style={{ width: 120 }}>{t('cardver.metalabel')}</th>
                <th>{t('cardver.lineage')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={it.topic_active === false ? { opacity: 0.7 } : undefined}>
                  <td>{it.card_number}</td>
                  <td>
                    {it.name}
                    {it.topic_active === false && (
                      <span className="badge badge-muted" style={{ marginLeft: 6 }}>{t('cardver.archived')}</span>
                    )}
                  </td>
                  <td className="save-indicator">{it.category ?? '—'}</td>
                  <td>
                    {it.metalabel && (
                      <span className={`badge ${it.metalabel === 'base' ? 'badge-accent' : 'badge-muted'}`}>
                        {it.metalabel}
                      </span>
                    )}
                  </td>
                  <td>
                    {it.change_type === 'dropped' ? (
                      <span className="badge badge-muted" style={{ color: 'var(--err)' }}>{t('cardver.dropped')}</span>
                    ) : it.successor_name ? (
                      <span>
                        v{it.successor_version} · {it.successor_number}. {it.successor_name}
                        <span className="save-indicator" style={{ marginLeft: 6 }}>({changeLabel(it.change_type)})</span>
                      </span>
                    ) : (
                      <span className="save-indicator">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
